// src/services/datosHistoricos.js

import AuthService from "../auth/auth.js";

class DatosHistoricosService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || process.env.VUE_APP_API_BASE_URL || "https://efibox.efintel.cl";
    this.endpoint = "/mensajes/api/mensajes_historico/";
    this.mapeoVariables = {
      tempRetorno: "Temp Retorno",
      tempInyeccion: "Temp Inyeccion",
      tempExterior: "Temp Exterior",
    };
  }

  // ==========================================
  // MAPEO DE MODOS - ACTUALIZADO
  // ==========================================

  /**
   * NUEVO: Mapear valores históricos de modo (del endpoint tipo_sensor=Modo)
   */
  mapearValorHistoricoModo(valorMinimo) {
    // Valores del endpoint histórico:
    // 0.0 = OFF, 0.1 = FRIO, 0.2 = CALOR, 0.3 = VENTILACION
    if (valorMinimo <= 0.05) return { texto: "OFF", numerico: 0 };
    if (valorMinimo <= 0.15) return { texto: "FRIO", numerico: 1 };
    if (valorMinimo <= 0.25) return { texto: "CALOR", numerico: 2 };
    if (valorMinimo <= 0.35) return { texto: "VENTILACION", numerico: 3 };

    return { texto: "OFF", numerico: 0 }; // Fallback
  }

  /**
   * CORREGIDO: Mapear modo numérico del backend a texto (consistente con frontend)
   */
  mapearModoNumericoATexto(modo) {
    const modos = {
      0: "OFF",
      1: "FRIO",
      2: "CALOR",
      3: "VENTILACION",
    };
    return modos[modo] || "OFF";
  }

  /**
   * CORREGIDO: Mapear modos a valores numéricos para el eje Y (0-3)
   */
  mapearEstadoANumero(estadoPrincipal, modoOperacion) {
    // Si está desactivado, siempre es OFF (0)
    if (estadoPrincipal === "desactivado") {
      return 0;
    }

    // Si está activado, mapear según el modo
    if (estadoPrincipal === "activado") {
      const mapeoModos = {
        OFF: 0,
        FRIO: 1,
        CALOR: 2,
        VENTILACION: 3,
      };
      return mapeoModos[modoOperacion] || 0;
    }

    return 0;
  }

  /**
   * CORREGIDO: Obtener etiquetas para el eje Y de modos (0-3) - CONSISTENTE
   */
  obtenerEtiquetasEstados() {
    return [
      { valor: 0, etiqueta: "OFF", color: "#6b7280" },
      { valor: 1, etiqueta: "FRIO", color: "#3b82f6" },
      { valor: 2, etiqueta: "CALOR", color: "#ef4444" },
      { valor: 3, etiqueta: "VENTILACION", color: "#10b981" },
    ];
  }

  // ==========================================
  // PROCESAMIENTO DE DATOS HISTÓRICOS - NUEVO
  // ==========================================

  /**
   * NUEVO: Procesar respuesta de datos históricos de MODO
   */
  procesarDatosHistoricosModo(respuesta) {
    const datos = this.extraerArregloDatos(respuesta);
    if (datos.length === 0) return [];

    return datos
      .map((punto) => {
        try {
          const valorPromedio = punto.promedio ?? 0.0;
          const modoMapeado = this.mapearValorHistoricoModo(valorPromedio);

          return {
            timestamp: new Date(punto.periodo),
            fecha: punto.periodo,
            modo_operacion: modoMapeado.texto,
            modo_numerico: modoMapeado.numerico,
            estado_numerico: modoMapeado.numerico,
            valor_original: valorPromedio,
            promedio: punto.promedio || 0,
            cantidad: punto.cantidad || 1,
            _tipo: "modo_historico",
            _fuente: "endpoint_historico",
          };
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);
  }

  /**
   * NUEVO: Procesar respuesta de datos históricos de DESHIELO
   */
  procesarDatosHistoricosDeshielo(respuesta) {
    const datos = this.extraerArregloDatos(respuesta);
    if (datos.length === 0) return [];

    return datos
      .map((punto) => {
        try {
          const valorMinimo = punto.minimo || 0.0;
          const deshieloActivo = valorMinimo > 0.5;

          return {
            timestamp: new Date(punto.periodo),
            fecha: punto.periodo,
            deshielo_activo: deshieloActivo,
            deshielo_numerico: deshieloActivo ? 1 : 0,
            valor_original: valorMinimo,
            promedio: punto.promedio || 0,
            cantidad: punto.cantidad || 1,
            _tipo: "deshielo_historico",
            _fuente: "endpoint_historico",
          };
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);
  }

  // ==========================================
  // PROCESAMIENTO DE ESTADOS ANTIGUO (FALLBACK) - CORREGIDO
  // ==========================================
  /**
   * CORREGIDO: Procesar respuesta de estados con deshielo separado y logging mejorado
   * NOTA: Este método se usa como fallback si no hay datos históricos específicos
   */
  procesarEstadosBackend(respuesta) {
    const datos = this.extraerArregloDatos(respuesta);
    if (datos.length === 0) return [];

    return datos
      .map((punto) => {
        try {
          const configuracion = punto.configuracion_dispositivo || {};
          const modo = configuracion.Modo !== undefined ? configuracion.Modo : 0;
          const occupied = configuracion.Occupied !== false;

          const deshielo = Boolean(configuracion.Deshielo || configuracion.deshielo || configuracion.DESHIELO || false);

          const estadoPrincipal = occupied ? "activado" : "desactivado";
          const modoOperacion = this.mapearModoNumericoATexto(modo);
          const estadoNumerico = this.mapearEstadoANumero(estadoPrincipal, modoOperacion);

          return {
            timestamp: new Date(punto.periodo),
            fecha: punto.periodo,
            estado_principal: estadoPrincipal,
            modo_operacion: modoOperacion,
            modo_numerico: modo,
            occupied: occupied,
            setpoint: configuracion.Setpoint || 22,
            estado_numerico: estadoNumerico,
            deshielo_activo: deshielo,
            deshielo_numerico: deshielo ? 1 : 0,
            temp_retorno: configuracion["Temp Retorno"] || null,
            temp_inyeccion: configuracion["Temp Inyeccion"] || null,
            valor: estadoNumerico,
            _tipo: "estado",
            _configuracion_original: configuracion,
            _fuente: "backend_fallback",
          };
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);
  }

  // ==========================================
  // OBTENCIÓN DE DATOS HISTÓRICOS - ACTUALIZADO
  // ==========================================

  /**
   * NUEVO: Obtener datos históricos de MODO (usando tipo_sensor=Modo)
   */
  async obtenerDatosHistoricosModo(dispositivoId, fechaDesde, fechaHasta, agregacion = "minuto", signal = null) {
    const fechaInicioISO = this.formatearFechaParaBackend(fechaDesde);
    const fechaFinISO = this.formatearFechaParaBackend(fechaHasta);

    const params = new URLSearchParams({
      dispositivo: dispositivoId.toString(),
      fecha_inicio: fechaInicioISO,
      fecha_fin: fechaFinISO,
      agregacion: agregacion,
      tipo_sensor: "Modo",
    });

    const url = `${this.endpoint}?${params.toString()}`;

    const response = await AuthService.authenticatedRequest(url, { signal });

    return this.procesarDatosHistoricosModo(response);
  }

  /**
   * NUEVO: Obtener datos históricos de DESHIELO (si existe tipo_sensor=Deshielo)
   */
  async obtenerDatosHistoricosDeshielo(dispositivoId, fechaDesde, fechaHasta, agregacion = "minuto", signal = null) {
    try {
      const fechaInicioISO = this.formatearFechaParaBackend(fechaDesde);
      const fechaFinISO = this.formatearFechaParaBackend(fechaHasta);

      const params = new URLSearchParams({
        dispositivo: dispositivoId.toString(),
        fecha_inicio: fechaInicioISO,
        fecha_fin: fechaFinISO,
        agregacion: agregacion,
        tipo_sensor: "Deshielo",
      });

      const url = `${this.endpoint}?${params.toString()}`;
      const response = await AuthService.authenticatedRequest(url, { signal });

      return this.procesarDatosHistoricosDeshielo(response);
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      return [];
    }
  }

  async obtenerDatosHistoricosPorVariable(dispositivoId, tipoSensor, fechaDesde, fechaHasta, agregacion = "minuto", signal = null) {
    const tipoSensorBackend = this.mapeoVariables[tipoSensor] || tipoSensor;
    const fechaInicioISO = this.formatearFechaParaBackend(fechaDesde);
    const fechaFinISO = this.formatearFechaParaBackend(fechaHasta);

    const params = new URLSearchParams({
      dispositivo: dispositivoId.toString(),
      fecha_inicio: fechaInicioISO,
      fecha_fin: fechaFinISO,
      agregacion: agregacion,
      tipo_sensor: tipoSensorBackend,
    });

    const url = `${this.endpoint}?${params.toString()}`;
    const response = await AuthService.authenticatedRequest(url, { signal });

    return this.procesarRespuestaBackend(response, tipoSensor);
  }

  async obtenerDatosHistoricosMultiples(dispositivoId, variables, fechaDesde, fechaHasta, agregacion = "minuto", signal = null) {
    const datosCombinados = {};

    // Ejecución secuencial en lugar de Promise.all para no saturar los workers del backend
    for (const variable of variables) {
      try {
        datosCombinados[variable] = await this.obtenerDatosHistoricosPorVariable(
          dispositivoId,
          variable,
          fechaDesde,
          fechaHasta,
          agregacion,
          signal,
        );
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        datosCombinados[variable] = [];
      }
    }

    return datosCombinados;
  }

  async obtenerEstadosDispositivo(dispositivoId, fechaDesde, fechaHasta, agregacion = "minuto") {
    const fechaInicioISO = this.formatearFechaParaBackend(fechaDesde);
    const fechaFinISO = this.formatearFechaParaBackend(fechaHasta);

    const params = new URLSearchParams({
      dispositivo: dispositivoId.toString(),
      fecha_inicio: fechaInicioISO,
      fecha_fin: fechaFinISO,
      agregacion: agregacion,
      incluir_estados: "true",
    });

    const url = `${this.endpoint}?${params.toString()}`;
    const response = await AuthService.authenticatedRequest(url);

    return this.procesarEstadosBackend(response);
  }

  /**
   * CORREGIDO: Obtener datos completos USANDO ENDPOINT HISTÓRICO DE MODO
   */
  async obtenerDatosCompletosSecuencial(dispositivoId, variables, fechaDesde, fechaHasta, agregacion = "minuto", signal = null) {
    // 1. Obtener temperaturas secuencialmente
    const datosTemperaturas = await this.obtenerDatosHistoricosMultiples(
      dispositivoId,
      variables,
      fechaDesde,
      fechaHasta,
      agregacion,
      signal,
    );

    // 2. Obtener Modos
    let datosModoHistorico = [];
    try {
      datosModoHistorico = await this.obtenerDatosHistoricosModo(dispositivoId, fechaDesde, fechaHasta, agregacion, signal);
    } catch (error) {
      if (error?.name === "AbortError") throw error;
    }

    // 3. Obtener Deshielo
    let datosDeshieloHistorico = [];
    try {
      datosDeshieloHistorico = await this.obtenerDatosHistoricosDeshielo(dispositivoId, fechaDesde, fechaHasta, agregacion, signal);
    } catch (error) {
      if (error?.name === "AbortError") throw error;
    }

    const tieneModosValidos = datosModoHistorico.some((m) => m.estado_numerico > 0);

    if (!tieneModosValidos) {
      let estadoActual = [];
      try {
        estadoActual = await this.obtenerEstadoActualParaGrafico(dispositivoId, fechaDesde, fechaHasta, datosTemperaturas);
      } catch (error) {
        if (error?.name === "AbortError") throw error;
      }

      return {
        temperaturas: datosTemperaturas,
        estados: estadoActual,
        deshielo: datosDeshieloHistorico,
        combinados: this.combinarDatosConEstadosHistoricos(datosTemperaturas, estadoActual, datosDeshieloHistorico),
      };
    }

    return {
      temperaturas: datosTemperaturas,
      estados: datosModoHistorico,
      deshielo: datosDeshieloHistorico,
      combinados: this.combinarDatosConEstadosHistoricos(datosTemperaturas, datosModoHistorico, datosDeshieloHistorico),
    };
  }

  // Generar datos de modo usando estado actual del dispositivo
  async obtenerEstadoActualParaGrafico(dispositivoId, fechaDesde, fechaHasta, datosTemperaturas) {
    try {
      // Obtener dispositivo actual
      const response = await AuthService.authenticatedRequest(`/dispositivos/api/dispositivos/${dispositivoId}/`);
      const modoActual = response?.configuracion_dispositivo?.Modo ?? 0;
      const modoTexto = this.mapearModoNumericoATexto(modoActual);

      // Crear puntos con el modo actual para cada timestamp de temperatura
      const variableBase = Object.keys(datosTemperaturas)[0];
      return datosTemperaturas[variableBase].map((punto) => ({
        timestamp: punto.timestamp,
        fecha: punto.fecha,
        modo_operacion: modoTexto,
        modo_numerico: modoActual,
        estado_numerico: modoActual,
        _tipo: "modo_actual",
        _fuente: "estado_dispositivo",
      }));
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      return [];
    }
  }

  // ==========================================
  // UTILIDADES - ACTUALIZADO
  // ==========================================
  extraerArregloDatos(respuesta) {
    if (!respuesta) return [];
    if (Array.isArray(respuesta)) return respuesta;
    return respuesta.resultados || respuesta.results || [];
  }
  /**
   * NUEVO: Combinar datos con estados históricos (modo + deshielo por separado)
   */
  combinarDatosConEstadosHistoricos(datosTemperaturas, datosModoHistorico, datosDeshieloHistorico) {
    const datosCombinados = this.combinarDatosParaGrafico(datosTemperaturas);

    return datosCombinados.map((punto) => {
      // Buscar modo histórico correspondiente (tolerancia de 1 minuto)
      const modoCorrespondiente = datosModoHistorico.find((modo) => Math.abs(modo.timestamp.getTime() - punto.timestamp.getTime()) < 60000);

      // Buscar deshielo histórico correspondiente
      const deshieloCorrespondiente = datosDeshieloHistorico.find(
        (deshielo) => Math.abs(deshielo.timestamp.getTime() - punto.timestamp.getTime()) < 60000,
      );

      // Combinar todos los datos
      const puntoCombinado = { ...punto };

      if (modoCorrespondiente) {
        puntoCombinado.modo_operacion = modoCorrespondiente.modo_operacion;
        puntoCombinado.modo_numerico = modoCorrespondiente.modo_numerico;
        puntoCombinado.estado_numerico = modoCorrespondiente.estado_numerico;
      } else {
        puntoCombinado.modo_operacion = "OFF";
        puntoCombinado.estado_numerico = 0;
      }

      if (deshieloCorrespondiente) {
        puntoCombinado.deshielo_activo = deshieloCorrespondiente.deshielo_activo;
        puntoCombinado.deshielo_numerico = deshieloCorrespondiente.deshielo_numerico;
      } else {
        puntoCombinado.deshielo_activo = false;
        puntoCombinado.deshielo_numerico = 0;
      }

      return puntoCombinado;
    });
  }

  procesarRespuestaBackend(respuesta, tipoVariable) {
    const datos = this.extraerArregloDatos(respuesta);
    if (datos.length === 0) return [];

    return datos.map((punto) => ({
      timestamp: new Date(punto.periodo),
      fecha: punto.periodo,
      promedio: punto.promedio || 0,
      [tipoVariable]: punto.promedio || 0,
      valor: punto.promedio || 0,
      _variable: tipoVariable,
      _fuente: "backend",
    }));
  }

  combinarDatosConEstados(datosTemperaturas, datosEstados) {
    const datosCombinados = this.combinarDatosParaGrafico(datosTemperaturas);

    return datosCombinados.map((punto) => {
      const estadoCorrespondiente = datosEstados.find((estado) => Math.abs(estado.timestamp.getTime() - punto.timestamp.getTime()) < 60000);

      if (estadoCorrespondiente) {
        return {
          ...punto,
          modo_operacion: estadoCorrespondiente.modo_operacion,
          modo_numerico: estadoCorrespondiente.modo_numerico,
          estado_numerico: estadoCorrespondiente.estado_numerico,
          deshielo_activo: estadoCorrespondiente.deshielo_activo,
          deshielo_numerico: estadoCorrespondiente.deshielo_numerico,
        };
      }

      return {
        ...punto,
        modo_operacion: "OFF",
        estado_numerico: 0,
        deshielo_activo: false,
        deshielo_numerico: 0,
      };
    });
  }

  combinarDatosParaGrafico(datosMultiples) {
    const variablesConDatos = Object.keys(datosMultiples).filter((variable) => datosMultiples[variable].length > 0);

    if (variablesConDatos.length === 0) return [];

    const variableBase = variablesConDatos[0];
    const datosBase = datosMultiples[variableBase];

    return datosBase.map((puntoBase) => {
      const puntoCombinado = {
        timestamp: puntoBase.timestamp,
        fecha: puntoBase.fecha,
      };

      variablesConDatos.forEach((variable) => {
        const puntoCorrespondiente = datosMultiples[variable].find(
          (punto) => Math.abs(punto.timestamp.getTime() - puntoBase.timestamp.getTime()) < 60000,
        );

        if (puntoCorrespondiente) {
          puntoCombinado[variable] = puntoCorrespondiente.promedio || 0;
        } else {
          puntoCombinado[variable] = null;
        }
      });

      return puntoCombinado;
    });
  }

  formatearFechaParaBackend(fecha) {
    if (!fecha) return null;

    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    const year = fechaObj.getFullYear();
    const month = String(fechaObj.getMonth() + 1).padStart(2, "0");
    const day = String(fechaObj.getDate()).padStart(2, "0");
    const hours = String(fechaObj.getHours()).padStart(2, "0");
    const minutes = String(fechaObj.getMinutes()).padStart(2, "0");
    const seconds = String(fechaObj.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}-03:00`;
  }
}

export default new DatosHistoricosService();
